"use client";

import { fetchCartItemsClient } from "@/api/saleor/services/checkoutClient";
import { getWishlistedProductsClient } from "@/api/saleor/services/wishlistClient";
import { algoliaInit } from "@/apiUtils/algoliaUtils";
import { getCartIdByLocale, hasArrayElements } from "@/apiUtils/commonUtils";
import { getFromCookieMetadata } from "@/apiUtils/cookieDataUtils";
import {
  FooterContent,
  SocialMediaIcon,
  TypeSessionUser,
  WishlistResponse,
} from "@/common/lib/types";
import { useGlobalStore } from "@/store/store";
import { syncCartVariantIds } from "@/utils/cart.utils";
import { getClientCookie, getLangFromLocale } from "@/utils/common";
import { useParams, usePathname } from "next/navigation";
import { ReactNode, useCallback, useEffect } from "react";
import Footer from "../components/footer/Footer";
import { updateCheckoutLanguageClient } from "@/api/saleor/services/checkoutClient";

interface IProps {
  children: ReactNode;
  footerContent: FooterContent;
  socialMediaIcons: SocialMediaIcon[];
  translations?: Record<string, string>;
}

const MainLayout = ({ children, footerContent, socialMediaIcons, translations }: IProps) => {
  if ("window" in globalThis) {
    // console.log("In top if block");
    // console.log("ENV", process?.env?.NEXT_PUBLIC_ENV);
    if (
      /^p.*r.*o.*d/i.test(process?.env?.NEXT_PUBLIC_ENV ?? "PROD") ||
      /^b.*e.*t.*a/i.test(process?.env?.NEXT_PUBLIC_ENV ?? "BETA")
    ) {
      // console.log("Inside event handler");
      // console.log("Disabling Logs");
      // console.log = () => {};
      // console.info = () => {};
      // console.warn = () => {};
    }
  }

  // hooks

  const pathname = usePathname();
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);
  const initWishlist = useGlobalStore((state) => state.initWishlist);
  const { user, setUser, setIsAuthenticated, updateCartCount, setAvailableSocialLogins } =
    useGlobalStore();

  // consts
  const isCheckout = Boolean(pathname.split("/").find((path) => path === "checkout"));
  const isBookEyeTest = Boolean(pathname.split("/").find((path) => path === "book-eyetest"));
  const isInstantPayment = Boolean(pathname.split("/").find((path) => path === "instantpayment"));
  const isForgotPassword = Boolean(pathname.split("/").find((path) => path === "forgot-password"));

  // methods
  const fetchWishlistedProducts = useCallback(async () => {
    const wishlistResponse: WishlistResponse | null =
      (await getWishlistedProductsClient(10)) || null;
    const wishlistedProducts: string[] =
      wishlistResponse?.items && hasArrayElements(wishlistResponse?.items?.edges)
        ? wishlistResponse?.items?.edges?.map((item: any) => item?.node?.variant?.id)
        : [];

    initWishlist(wishlistedProducts);
  }, []);

  const fetchCartItems = useCallback(async () => {
    const {count, lines} = await fetchCartItemsClient();
    updateCartCount(count);
    syncCartVariantIds(lines);
  }, []);

  const handleUpdateCheckoutLanguage = useCallback(async () => {
    const cartId = getFromCookieMetadata(getCartIdByLocale(locale as string));
    if (cartId) {
      await updateCheckoutLanguageClient(cartId, lang.toUpperCase());
    }
  }, [locale]);

  // effects
  useEffect(() => {
    fetchWishlistedProducts();
    fetchCartItems();
  }, [user, locale]);

  useEffect(() => {
    const regex = new RegExp("^(\/(sa|ae)-(en|ar))?\/order");
    const isOrderPage = regex.test(pathname);
    if (isOrderPage) fetchCartItems();
  }, [pathname]);

  useEffect(() => {
    handleUpdateCheckoutLanguage();
  }, [locale]);

  // useEffect(() => {
  //   if(availableSocialLogins.length === 0) return;
  //   setAvailableSocialLogins(availableSocialLogins)
  // },[availableSocialLogins])

  useEffect(() => {
    const token = getClientCookie("token");

    if (token) {
      // assuming this is jwt token, decode it and store it into an object
      // write the code to decode this token on client and store it into an object
      const [, payload] = token.split(".");
      const decodedAscii = atob(payload);
      const bytesLen = decodedAscii.length;
      const bytes = new Uint8Array(bytesLen);

      for (let i = 0; i < bytesLen; i++) bytes[i] = decodedAscii.charCodeAt(i);

      const decoder = new TextDecoder("utf-8");
      const decoded = decoder.decode(bytes);
      const payloadData = JSON.parse(decoded) as TypeSessionUser;

      setUser(payloadData);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    algoliaInit();
  }, []);

  // console.log({ isCheckout, isInstantPayment, isBookEyeTest });

  return (
    <>
      <main>{children}</main>
      {!isCheckout && !isInstantPayment && !isForgotPassword ? (
        <Footer
          footerContent={footerContent}
          translations={translations}
          socialMediaIcons={socialMediaIcons}
          locale={locale as string}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default MainLayout;
